import React, { FC } from "react";
import {
  PlusOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  ExpandOutlined,
  CompressOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Modal, Typography, Tooltip } from "antd";
import styles from "./workspace-header.css";
import clsx from "clsx";
import { CustomPromptModal } from "./custom-prompt-modal";
const { ["container"]: containerClassName, ["border"]: borderClassName } = styles as any;

export type WorkspaceHeaderProps<T extends boolean> = {
  libraryOpen: boolean;
  setLibraryOpen?: (open: boolean) => void;
  addNew: T extends true ? () => void : undefined;
  showAddNew: T;
  reset: T extends true ? () => void : undefined;
  showReset: boolean;
  labelOverride?: string;
  onCollapseAll?: () => void;
  onExpandAll?: () => void;
  showCollapseExpand?: boolean;
  somePhases?: boolean;
  handleAutoGenerate?: (
    useLibraryData: boolean,
    refineMode: boolean,
    customPrompt: string | null
  ) => void;
  customPrompt?: string;
  setCustomPrompt?: (prompt: string) => void;
  customModalVisible?: boolean;
  setCustomModalVisible?: (visible: boolean) => void;
};

const confirmClearScope = (cb: any) => {
  Modal.confirm({
    width: 600,
    icon: <ExclamationCircleOutlined />,
    content: (
      <Typography.Text>{`Are you sure you want to clear all phases and tasks?`}</Typography.Text>
    ),
    onOk: async function () {
      cb();
    },
    onCancel() {},
  });
};

export const WorkspaceHeader = <T extends boolean>({
  addNew,
  showAddNew,
  reset,
  showReset,
  labelOverride,
  onCollapseAll,
  onExpandAll,
  showCollapseExpand = false,
  somePhases = false,
  handleAutoGenerate,
  customPrompt,
  setCustomPrompt,
  customModalVisible,
  setCustomModalVisible,
}: WorkspaceHeaderProps<T>): ReturnType<FC<WorkspaceHeaderProps<T>>> => {
  return (
    <>
      <div
        className={clsx(containerClassName, showAddNew && borderClassName)}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          {showCollapseExpand && (
            <>
              <Tooltip title="Collapse All">
                <Button
                  icon={<CompressOutlined />}
                  size="large"
                  onClick={onCollapseAll}
                  style={{ marginRight: "10px" }}
                />
              </Tooltip>
              <Tooltip title="Expand All">
                <Button
                  icon={<ExpandOutlined />}
                  size="large"
                  onClick={onExpandAll}
                  style={{ marginRight: "10px" }}
                />
              </Tooltip>
            </>
          )}
        </div>
        <div>
          {showReset && somePhases && (
            <Button
              icon={<ThunderboltOutlined />}
              size="large"
              type="primary"
              onClick={() => setCustomModalVisible?.(true)}
              style={{ marginRight: "10px" }}
            >
              Refine
            </Button>
          )}
          {showReset && (
            <Button
              icon={<ReloadOutlined />}
              size="large"
              type="primary"
              onClick={() => confirmClearScope(reset)}
              style={{ marginRight: "10px" }}
            >
              Clear All
            </Button>
          )}
          {showAddNew && (
            <Button icon={<PlusOutlined />} size="large" type="primary" onClick={addNew}>
              {labelOverride ?? "Add New"}
            </Button>
          )}
        </div>
      </div>
      {setCustomPrompt && setCustomModalVisible && handleAutoGenerate && (
        <CustomPromptModal
          customModalVisible={customModalVisible ?? false}
          customPrompt={customPrompt ?? ""}
          setCustomPrompt={setCustomPrompt ?? (() => {})}
          setCustomModalVisible={setCustomModalVisible ?? (() => {})}
          refineMode={somePhases}
          handleAutoGenerate={handleAutoGenerate}
        />
      )}
    </>
  );
};
